import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { Product, ProductCategory, ProductStatus } from '../mocks/products';

function rowToProduct(r: Record<string, unknown>): Product {
  return {
    id: r.id as string,
    category: ((r.category as string) || 'Package') as ProductCategory,
    name: (r.name as string) || '',
    destination: (r.destination as string) || '',
    duration: '',
    description: (r.description as string) || '',
    image: '',
    status: (((r.status as string) || 'active').charAt(0).toUpperCase() + ((r.status as string) || 'active').slice(1)) as ProductStatus,
    currency: (r.currency as string) || 'AED',
    costPrice: (r.cost_price as number) || 0,
    sellPrice: (r.sell_price as number) || 0,
    minPax: 1,
    maxPax: 20,
    inclusions: [],
    exclusions: [],
    supplierName: '',
    supplierContact: '',
    supplierRef: '',
    timesBooked: 0,
    rating: 0,
    tags: (r.tags as string[]) || [],
    featured: false,
    availableFrom: '',
    availableTo: '',
  };
}

export function useProducts() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('products').select('*').order('created_at', { ascending: false });
    setProducts((data || []).map((r) => rowToProduct(r as Record<string, unknown>)));
    setLoading(false);
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const createProduct = async (product: Partial<Product>) => {
    const { data, error } = await supabase.from('products').insert({
      name: product.name,
      category: product.category,
      description: product.description,
      destination: product.destination,
      cost_price: product.costPrice || 0,
      sell_price: product.sellPrice || 0,
      currency: product.currency || 'AED',
      status: (product.status || 'Active').toLowerCase(),
      tags: product.tags || [],
    }).select().maybeSingle();
    if (!error && data) setProducts((prev) => [rowToProduct(data as Record<string, unknown>), ...prev]);
    return { data, error };
  };

  const updateProduct = async (id: string, updates: Partial<Product>) => {
    const { error } = await supabase.from('products').update({
      name: updates.name,
      category: updates.category,
      description: updates.description,
      destination: updates.destination,
      cost_price: updates.costPrice,
      sell_price: updates.sellPrice,
      status: updates.status?.toLowerCase(),
      tags: updates.tags,
    }).eq('id', id);
    if (!error) setProducts((prev) => prev.map((p) => p.id === id ? { ...p, ...updates } : p));
  };

  const deleteProduct = async (id: string) => {
    const { error } = await supabase.from('products').delete().eq('id', id);
    if (!error) setProducts((prev) => prev.filter((p) => p.id !== id));
  };

  return { products, loading, refetch: fetchProducts, createProduct, updateProduct, deleteProduct };
}
